import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import {
  PhysicsWorld,
  COLLISION_GROUP_AI_VEHICLE,
  COLLISION_GROUP_CHASSIS,
  COLLISION_GROUP_GROUND,
  COLLISION_GROUP_STATIC,
  COLLISION_GROUP_DYNAMIC_PROP,
} from '../core/PhysicsWorld';
import { TrafficLight } from '../environment/TrafficLights';
import { AIPedestrian } from './AIPedestrian';
import { ObstacleVehicle } from './AIVehicle';

export type TwoWheelerType = 'bicycle' | 'scooter';

export class AICyclist {
  public mesh: THREE.Group;
  public position = new THREE.Vector3();
  public speedKmh: number = 18;
  public cruiseSpeedKmh: number = 18;
  public waypoints: THREE.Vector3[] = [];
  public currentWaypointIdx: number = 0;
  public type: TwoWheelerType;
  public body: CANNON.Body | null = null;

  private wheelFront!: THREE.Mesh;
  private wheelRear!: THREE.Mesh;
  private animTime: number = 0;

  constructor(
    type: TwoWheelerType,
    colorHex: number,
    waypoints: THREE.Vector3[],
    speedKmh: number = 18,
    physicsWorld?: PhysicsWorld
  ) {
    this.type = type;
    this.waypoints = waypoints;
    this.cruiseSpeedKmh = speedKmh;
    this.speedKmh = speedKmh;
    this.currentWaypointIdx = waypoints.length > 1 ? 1 : 0;
    this.position.copy(waypoints[0] || new THREE.Vector3());

    this.mesh = new THREE.Group();
    this.buildMesh(colorHex);
    this.mesh.position.copy(this.position);

    if (physicsWorld) {
      this.initPhysicsBody(physicsWorld);
    }
  }

  private initPhysicsBody(physicsWorld: PhysicsWorld) {
    const cyclistShape = new CANNON.Box(new CANNON.Vec3(0.35, 0.70, 0.90));
    this.body = new CANNON.Body({
      type: CANNON.Body.KINEMATIC,
      material: physicsWorld.aiVehicleMaterial,
      collisionFilterGroup: COLLISION_GROUP_AI_VEHICLE,
      collisionFilterMask:
        COLLISION_GROUP_CHASSIS |
        COLLISION_GROUP_GROUND |
        COLLISION_GROUP_STATIC |
        COLLISION_GROUP_DYNAMIC_PROP,
    });
    this.body.addShape(cyclistShape, new CANNON.Vec3(0, 0.70, 0));
    this.body.position.set(this.position.x, this.position.y, this.position.z);
    physicsWorld.world.addBody(this.body);
  }

  private buildMesh(frameColorHex: number) {
    const frameMat = new THREE.MeshStandardMaterial({
      color: frameColorHex,
      metalness: 0.6,
      roughness: 0.3,
    });
    const rubberMat = new THREE.MeshStandardMaterial({ color: 0x1e293b, roughness: 0.9 });
    const chromeMat = new THREE.MeshStandardMaterial({ color: 0xe2e8f0, metalness: 0.9, roughness: 0.1 });
    const riderJacketMat = new THREE.MeshStandardMaterial({ color: 0x0284c7, roughness: 0.6 });
    const helmetMat = new THREE.MeshStandardMaterial({ color: 0xfacc15, roughness: 0.3 }); // High-vis yellow helmet
    const skinMat = new THREE.MeshStandardMaterial({ color: 0xfcd34d, roughness: 0.6 });
    const pantsMat = new THREE.MeshStandardMaterial({ color: 0x1e293b, roughness: 0.7 });

    const wheelRadius = this.type === 'bicycle' ? 0.33 : 0.14;
    const wheelGeom = new THREE.CylinderGeometry(wheelRadius, wheelRadius, 0.05, 16);

    // Front & Rear Wheels
    this.wheelFront = new THREE.Mesh(wheelGeom, rubberMat);
    this.wheelFront.rotation.z = Math.PI / 2;
    this.wheelFront.position.set(0, wheelRadius, 0.55);
    this.wheelFront.castShadow = true;

    this.wheelRear = new THREE.Mesh(wheelGeom, rubberMat);
    this.wheelRear.rotation.z = Math.PI / 2;
    this.wheelRear.position.set(0, wheelRadius, -0.55);
    this.wheelRear.castShadow = true;

    this.mesh.add(this.wheelFront, this.wheelRear);

    if (this.type === 'bicycle') {
      // Diamond Bike Frame
      const frameGeom = new THREE.CylinderGeometry(0.02, 0.02, 1.0, 8);
      const topTube = new THREE.Mesh(frameGeom, frameMat);
      topTube.rotation.x = Math.PI / 2;
      topTube.position.set(0, 0.65, 0);
      topTube.scale.set(1, 0.9, 1);

      const downTube = new THREE.Mesh(frameGeom, frameMat);
      downTube.rotation.x = -Math.PI / 4;
      downTube.position.set(0, 0.45, 0.25);
      downTube.scale.set(1, 0.8, 1);

      const seatTube = new THREE.Mesh(frameGeom, frameMat);
      seatTube.position.set(0, 0.52, -0.2);
      seatTube.scale.set(1, 0.6, 1);

      // Handlebars
      const bar = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.03, 0.04), chromeMat);
      bar.position.set(0, 0.88, 0.48);

      // Saddle
      const saddle = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.04, 0.24), rubberMat);
      saddle.position.set(0, 0.72, -0.22);

      this.mesh.add(topTube, downTube, seatTube, bar, saddle);

      // Rider seated
      const torso = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.48, 0.2), riderJacketMat);
      torso.position.set(0, 1.02, -0.05);
      torso.rotation.x = 0.25; // Leaning slightly forward
      torso.castShadow = true;

      const head = new THREE.Mesh(new THREE.SphereGeometry(0.11, 12, 12), skinMat);
      head.position.set(0, 1.32, 0.05);

      const helmet = new THREE.Mesh(new THREE.SphereGeometry(0.125, 12, 12), helmetMat);
      helmet.position.set(0, 1.35, 0.05);
      helmet.scale.set(1.0, 0.8, 1.15);

      // Legs pedaling
      const legL = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.45, 0.1), pantsMat);
      legL.position.set(-0.14, 0.62, -0.1);
      const legR = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.45, 0.1), pantsMat);
      legR.position.set(0.14, 0.62, -0.1);

      this.mesh.add(torso, head, helmet, legL, legR);
    } else {
      // E-Scooter Deck & Stem
      const deck = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.06, 0.9), frameMat);
      deck.position.set(0, 0.15, 0);

      const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 0.95, 8), frameMat);
      stem.position.set(0, 0.62, 0.42);
      stem.rotation.x = -0.1;

      const bar = new THREE.Mesh(new THREE.BoxGeometry(0.48, 0.03, 0.04), chromeMat);
      bar.position.set(0, 1.05, 0.38);

      this.mesh.add(deck, stem, bar);

      // Rider standing upright
      const torso = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.52, 0.2), riderJacketMat);
      torso.position.set(0, 0.95, -0.02);
      torso.castShadow = true;

      const head = new THREE.Mesh(new THREE.SphereGeometry(0.11, 12, 12), skinMat);
      head.position.set(0, 1.28, 0.02);

      const helmet = new THREE.Mesh(new THREE.SphereGeometry(0.125, 12, 12), helmetMat);
      helmet.position.set(0, 1.31, 0.02);
      helmet.scale.set(1.0, 0.8, 1.15);

      const legs = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.52, 0.14), pantsMat);
      legs.position.set(0, 0.44, 0);

      this.mesh.add(torso, head, helmet, legs);
    }
  }

  public update(
    deltaTime: number,
    playerPos?: THREE.Vector3,
    playerSpeedKmh: number = 0,
    trafficLights?: TrafficLight[],
    pedestrians?: AIPedestrian[],
    otherVehicles?: ObstacleVehicle[]
  ) {
    if (this.waypoints.length === 0) return;

    const targetWp = this.waypoints[this.currentWaypointIdx];
    const toTarget = targetWp.clone().sub(this.position);
    const dist = toTarget.length();

    if (dist < 1.2) {
      this.currentWaypointIdx = (this.currentWaypointIdx + 1) % this.waypoints.length;
      return;
    }

    const dir = toTarget.clone().normalize();

    // ── 0. AI Vehicle / Bus Obstacle Detection ──────────────────────────────
    let vehicleRequiresStop = false;
    let distToVehicle = 999;
    let vehicleStopBuffer = 4.2;
    if (otherVehicles && otherVehicles.length > 0) {
      for (const other of otherVehicles) {
        const toOther = other.position.clone().sub(this.position);
        const alongDir = toOther.dot(dir);
        const lateral = Math.abs(-dir.z * toOther.x + dir.x * toOther.z);
        const reqBuf = (other.halfLength ?? 2.2) + 2.0;
        if (alongDir > 0.5 && alongDir < 14.0 && lateral < 2.2) {
          if (alongDir < distToVehicle) {
            distToVehicle = alongDir;
            vehicleStopBuffer = reqBuf;
            vehicleRequiresStop = true;
          }
        }
      }
    }

    // ── 1. Traffic Light Detection ──────────────────────────────────────────
    let lightRequiresStop = false;
    let distToLight = 999;
    if (trafficLights && trafficLights.length > 0) {
      for (const tl of trafficLights) {
        if (tl.phase === 'RED' || tl.phase === 'RED_YELLOW') {
          const align = dir.dot(tl.approachDirection);
          if (align > 0.7) {
            const toTl = tl.group.position.clone().sub(this.position);
            const alongDir = toTl.dot(dir);
            const lateral = Math.abs(-dir.z * toTl.x + dir.x * toTl.z);
            if (alongDir > 1.0 && alongDir < 16.0 && lateral < 5.0) {
              lightRequiresStop = true;
              distToLight = Math.min(distToLight, alongDir);
            }
          }
        }
      }
    }

    // ── 2. Pedestrian Yielding Detection ───────────────────────────────────
    let pedRequiresStop = false;
    let distToPed = 999;
    if (pedestrians && pedestrians.length > 0) {
      for (const ped of pedestrians) {
        const toPed = ped.position.clone().sub(this.position);
        const alongDir = toPed.dot(dir);
        const lateral = Math.abs(-dir.z * toPed.x + dir.x * toPed.z);
        if (alongDir > 0.8 && alongDir < 14.0 && lateral < 2.5) {
          pedRequiresStop = true;
          distToPed = Math.min(distToPed, alongDir);
        }
      }
    }

    // ── 3. Hard Obstacle & TTC Braking for Student Vehicle ──────────────────
    // Touching distance center-to-center is ~3.1m. Safe gap > 1.5m requires distance >= 4.8m.
    const SAFE_GAP_CENTER_DIST = 4.8;
    const DANGER_ZONE_DIST = 14.0;
    let dotFront = -1;
    let distToPlayer = 999;

    if (playerPos) {
      const toPlayer = playerPos.clone().sub(this.position);
      distToPlayer = toPlayer.length();
      dotFront = toPlayer.dot(dir);
      const lateralDist = Math.abs(-dir.z * toPlayer.x + dir.x * toPlayer.z);

      const playerInCorridor = dotFront > 0.5 && dotFront < DANGER_ZONE_DIST && lateralDist < 2.0;
      const isImminentProximity = dotFront > 0.5 && distToPlayer < SAFE_GAP_CENTER_DIST;

      if (isImminentProximity || playerInCorridor) {
        const closingSpeedKmh = Math.max(0.5, this.cruiseSpeedKmh - playerSpeedKmh);
        const availableDist = Math.max(0, distToPlayer - SAFE_GAP_CENTER_DIST);
        const ttc = availableDist / (closingSpeedKmh / 3.6);

        if (distToPlayer <= SAFE_GAP_CENTER_DIST || ttc < 2.5) {
          // Hard brake to full standstill — preserves safe gap > 1.5m
          this.speedKmh = 0;
        } else {
          // Progressive deceleration
          const t = Math.min(1.0, availableDist / (DANGER_ZONE_DIST - SAFE_GAP_CENTER_DIST));
          this.speedKmh = Math.max(0, this.cruiseSpeedKmh * t);
        }
      } else {
        // Accelerate smoothly back to cruise speed
        this.speedKmh = Math.min(this.cruiseSpeedKmh, this.speedKmh + 15.0 * deltaTime);
      }
    } else {
      this.speedKmh = this.cruiseSpeedKmh;
    }

    // AI Vehicle / Bus obstacle braking
    if (vehicleRequiresStop) {
      if (distToVehicle <= vehicleStopBuffer) {
        this.speedKmh = 0;
      } else {
        const t = Math.max(0, (distToVehicle - vehicleStopBuffer) / (14.0 - vehicleStopBuffer));
        this.speedKmh = Math.min(this.speedKmh, this.cruiseSpeedKmh * t);
        if (this.speedKmh < 3.0) {
          this.speedKmh = 0;
        }
      }
    }

    // Traffic light & pedestrian braking
    if (lightRequiresStop) {
      if (distToLight <= 4.0) {
        this.speedKmh = 0;
      } else {
        const t = Math.max(0, (distToLight - 4.0) / (16.0 - 4.0));
        this.speedKmh = Math.min(this.speedKmh, this.cruiseSpeedKmh * t);
        if (this.speedKmh < 3.0) {
          this.speedKmh = 0;
        }
      }
    }

    if (pedRequiresStop) {
      if (distToPed <= 3.5) {
        this.speedKmh = 0;
      } else {
        const t = Math.max(0, (distToPed - 3.5) / (14.0 - 3.5));
        this.speedKmh = Math.min(this.speedKmh, this.cruiseSpeedKmh * t);
        if (this.speedKmh < 3.0) {
          this.speedKmh = 0;
        }
      }
    }

    this.animTime += deltaTime;
    // Rotate wheels only when moving
    if (this.speedKmh > 0.1) {
      const rollSpeed = (this.speedKmh / 3.6) * deltaTime / 0.3;
      this.wheelFront.rotation.x += rollSpeed;
      this.wheelRear.rotation.x += rollSpeed;
    }

    let moveDist = (this.speedKmh / 3.6) * deltaTime;
    if (playerPos && dotFront > 0.5) {
      // Hard physical clamp: Never close the gap smaller than SAFE_GAP_CENTER_DIST
      const maxAllowedMove = Math.max(0, distToPlayer - SAFE_GAP_CENTER_DIST);
      moveDist = Math.min(moveDist, maxAllowedMove);
    }
    if (vehicleRequiresStop && distToVehicle <= vehicleStopBuffer) {
      moveDist = 0;
    }
    // Hard physical clamp: Never cross traffic light stop line on RED
    if (lightRequiresStop && distToLight <= 3.5) {
      moveDist = 0;
    }
    // Hard physical clamp: Never hit crossing pedestrian
    if (pedRequiresStop && distToPed <= 3.0) {
      moveDist = 0;
    }

    if (moveDist > 0.0001) {
      this.position.add(dir.clone().multiplyScalar(Math.min(moveDist, dist)));
      this.mesh.position.copy(this.position);
    }

    // Face travel direction
    const heading = Math.atan2(dir.x, dir.z);
    this.mesh.rotation.y = heading;

    if (this.body) {
      this.body.position.set(this.position.x, this.position.y, this.position.z);
      this.body.quaternion.setFromEuler(0, heading, 0);
    }
  }
}
